// The one way files reach the server: checks the upload limit (shared/src/uploads.ts) first, then POSTs
// to /api/upload. Errors carry a message fit for a toast.
import { uploadProblem } from '@shared/uploads';
import { toast } from '@/components/ui/overlays';

export interface Uploaded {
  url: string;
  size: number;
  name: string;
}

/** Upload a file into a workspace (by default the one in the current URL). */
export async function postUpload(file: File, workspaceId = location.pathname.split('/')[1] ?? ''): Promise<Uploaded> {
  const problem = uploadProblem(file);
  if (problem) throw new Error(problem);
  const res = await fetch('/api/upload', {
    method: 'POST',
    headers: { 'x-workspace-id': workspaceId, 'x-filename': encodeURIComponent(file.name), 'content-type': file.type || 'application/octet-stream' },
    body: file,
  });
  const data = (await res.json().catch(() => ({}))) as Partial<Uploaded> & { error?: string };
  if (!res.ok || !data.url) throw new Error(data.error ?? `Couldn't upload ${file.name}. Try again.`);
  return { url: data.url, size: data.size ?? file.size, name: data.name || file.name };
}

/** Upload your profile photo (already cropped and compressed); returns its address for Users.avatarUrl. */
export async function postAvatar(file: File): Promise<string> {
  const res = await fetch('/api/avatar', { method: 'POST', headers: { 'content-type': file.type }, body: file });
  const data = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
  if (!res.ok || !data.url) throw new Error(data.error ?? "Couldn't upload your photo. Try again.");
  return data.url;
}

/** The files that can be uploaded; says why any others can't. */
export function keepUploadable(files: File[]): File[] {
  const problems = files.map(uploadProblem).filter((p): p is string => !!p);
  if (problems.length) toast(problems.length === 1 ? problems[0] : `${problems.length} files can't be attached. ${problems[0]}`);
  return files.filter((f) => !uploadProblem(f));
}

/** The message to show for a failed upload. */
export const uploadError = (e: unknown, file: File) => (e instanceof Error && e.message ? e.message : `Couldn't upload ${file.name}.`);
